import { useApplicationConfiguration } from './useApplicationConfiguration';
import { useCallback, useMemo } from 'react';
import { compareStoresByDisplayId, formatStoreLabel } from '../lib/display/displayLib';

/**
 * Create label for store based on application configuration and store details
 * @param storeName Name of the store
 * @param storeCustomerId Customer id of the store
 * @param storeCode Code of the store (if codes are used in the project)
 */
const useDisplayStoreLabel = (
  storeName: string,
  storeCustomerId?: string | null,
  storeCode?: string | null,
): string => {
  const { displayStoreId } = useApplicationConfiguration();

  return useMemo(() => {
    return formatStoreLabel(displayStoreId, storeName, storeCustomerId, storeCode);
  }, [displayStoreId, storeCode, storeCustomerId, storeName]);
};

export type StoreLabelFormatter = (
  storeName: string,
  storeCustomerId?: string | null,
  storeCode?: string | null,
) => string;

/**
 * Return a function that creates label for store based on application configuration and store details.
 */
export const useDisplayStoreLabelFormatter = (): StoreLabelFormatter => {
  const { displayStoreId } = useApplicationConfiguration();

  return useCallback(
    (storeName: string, storeCustomerId?: string | null, storeCode?: string | null) => {
      return formatStoreLabel(displayStoreId, storeName, storeCustomerId, storeCode);
    },
    [displayStoreId],
  );
};

/**
 * Return a comparator that orders stores by the identifier their label starts with, then by name.
 */
export const useDisplayStoreComparator = (): ReturnType<typeof compareStoresByDisplayId> => {
  const { displayStoreId } = useApplicationConfiguration();

  return useMemo(() => compareStoresByDisplayId(displayStoreId), [displayStoreId]);
};

export default useDisplayStoreLabel;
